import React from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { Checkbox } from "react-native-paper";
import { useTranslation } from "react-i18next";

import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import SubmissionRow from "./SubmissionRow";
import { GradingCenterModalState } from "./types";
import { useGradingCenterSubmissions } from "../../../contexts/GradingCenterContext";
import {
  AssessmentSubmissionForGrading,
  LtiGradePassbackStatus,
  UnifiedSubmission,
} from "@axio/graphql/generated/graphql";
import { formatSubmittedDate } from "../../../utils/date";
import { getInitials } from "../../../utils/getInitials";

interface GradingCenterTableProps {
  setModalState?: React.Dispatch<React.SetStateAction<GradingCenterModalState>>;
  variant?: "default" | "lti-assessment";
  ltiAssessmentRows?: AssessmentSubmissionForGrading[];
  ltiAssessmentLoading?: boolean;
  selectedSubmissionIds?: string[];
  onToggleSubmissionSelection?: (submissionId: string) => void;
  onToggleSelectAllSubmissions?: () => void;
  onLtiEditGradePress?: (submission: AssessmentSubmissionForGrading) => void;
}

const DefaultGradingCenterTable: React.FC<{
  setModalState: React.Dispatch<React.SetStateAction<GradingCenterModalState>>;
}> = ({ setModalState }) => {
  const { filteredSubmissions, setSelectedSubmissionData, isLoading } = useGradingCenterSubmissions();

  const handleGradePress = (submission: UnifiedSubmission) => {
    setSelectedSubmissionData(submission);
    if (submission.submissionType === "Assignment") {
      setModalState(GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE);
    } else if (submission.submissionType === "Assessment") {
      setModalState(GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE);
    } else if (submission.submissionType === "Project") {
      setModalState(GradingCenterModalState.VIEW_PROJECT_RESPONSE);
    }
  };

  return (
    <View style={styles.tableDataContainer}>
      <View style={styles.table}>
        {/* Table Header */}
        <View style={styles.tableHeader}>
          <View style={[styles.cell, styles.studentCell]}>
            <CustomText weight="bold" text="Student" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.typeCell]}>
            <CustomText weight="bold" text="Submission Type" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.courseCell]}>
            <CustomText weight="bold" text="Course" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.moduleCell]}>
            <CustomText weight="bold" text="Submission Title" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.dateCell]}>
            <CustomText weight="bold" text="Submitted on" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.statusCell]}>
            <CustomText weight="bold" text="Status" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.gradeCell]}>
            <CustomText weight="bold" text="Grade" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.actionsCell]}>
            <CustomText weight="bold" text="Action" size="s" style={styles.headerText} />
          </View>
        </View>

        {/* Table Body */}
        {isLoading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        ) : filteredSubmissions?.length > 0 ? (
          filteredSubmissions.map((submission) => (
            <SubmissionRow key={submission.id} submission={submission} onGradePress={handleGradePress} />
          ))
        ) : (
          <View style={styles.emptyState}>
            <CustomText text="No data found" style={styles.emptyText} />
          </View>
        )}
      </View>
    </View>
  );
};

const LtiAssessmentGradingTable: React.FC<GradingCenterTableProps> = ({
  ltiAssessmentRows = [],
  ltiAssessmentLoading = false,
  selectedSubmissionIds = [],
  onToggleSubmissionSelection,
  onToggleSelectAllSubmissions,
  onLtiEditGradePress,
}) => {
  const { t } = useTranslation();
  const selectedCount = selectedSubmissionIds.length;
  const rowCount = ltiAssessmentRows.length;
  const allSelected = rowCount > 0 && selectedCount === rowCount;
  const headerCheckboxStatus = allSelected ? "checked" : selectedCount > 0 ? "indeterminate" : "unchecked";

  const getPassbackStatusText = (status: LtiGradePassbackStatus | null | undefined): string => {
    switch (status) {
      case LtiGradePassbackStatus.Posted:
        return t("text.lti_grade_passback_posted");
      case LtiGradePassbackStatus.Pending:
        return t("text.lti_grade_passback_pending");
      case LtiGradePassbackStatus.Failed:
        return t("text.lti_grade_passback_failed");
      default:
        // No status => never sent to the LMS, or edited after a prior post.
        return t("text.lti_grade_passback_not_published");
    }
  };

  return (
    <View style={styles.tableDataContainer}>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <View style={[styles.cell, styles.checkboxCell]}>
            <Checkbox
              status={headerCheckboxStatus}
              onPress={onToggleSelectAllSubmissions}
              disabled={rowCount === 0 || !onToggleSelectAllSubmissions}
              theme={{ colors: { primary: "#3E68FE" } }}
            />
          </View>
          <View style={[styles.cell, styles.studentCell]}>
            <CustomText weight="bold" text="Student" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.dateCell]}>
            <CustomText weight="bold" text="Submitted on" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.gradeCell]}>
            <CustomText weight="bold" text="Grade" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.postStatusCell]}>
            <CustomText weight="bold" text="Post Status" size="s" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.actionsCell]}>
            <CustomText weight="bold" text="Action" size="s" style={styles.headerText} />
          </View>
        </View>

        {ltiAssessmentLoading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        ) : ltiAssessmentRows.length > 0 ? (
          ltiAssessmentRows.map((submission) => {
            const isChecked = selectedSubmissionIds.includes(submission.id);
            const passbackStatus = submission.ltiGradePassbackStatus ?? null;
            const isPosted = passbackStatus === LtiGradePassbackStatus.Posted;
            const isFailed = passbackStatus === LtiGradePassbackStatus.Failed;
            const isPublishing = passbackStatus === LtiGradePassbackStatus.Pending;
            // No status => the LMS does not have the current grade: either it was
            // never posted, or it was edited after a prior post (the resolver
            // clears the status on edit). Shown as "Not published".
            const hasGrade = submission.totalPoints != null && submission.maximumPoints != null;
            const buttonText = hasGrade ? t("text.Edit Grade") : t("text.Grade");
            const postedStatusText = getPassbackStatusText(passbackStatus);

            return (
              <View key={submission.id} style={styles.tableRow}>
                <View style={[styles.cell, styles.checkboxCell]}>
                  <Checkbox
                    status={isChecked ? "checked" : "unchecked"}
                    onPress={() => onToggleSubmissionSelection?.(submission.id)}
                    disabled={!onToggleSubmissionSelection}
                    theme={{ colors: { primary: "#3E68FE" } }}
                  />
                </View>
                <View style={[styles.cell, styles.studentCell]}>
                  <View style={styles.userInfo}>
                    <View style={styles.userAvatar}>
                      <CustomText
                        text={getInitials(submission.userName)}
                        size="xs"
                        weight="bold"
                        style={styles.userInitials}
                        useTranslationText={false}
                      />
                    </View>
                    <View style={styles.userDetails}>
                      <CustomText
                        text={submission.userName || "Unknown Student"}
                        size="s"
                        style={styles.userName}
                        useTranslationText={false}
                      />
                      <CustomText
                        text={submission.userEmail || ""}
                        size="xs"
                        style={styles.userEmail}
                        useTranslationText={false}
                      />
                    </View>
                  </View>
                </View>
                <View style={[styles.cell, styles.dateCell]}>
                  <CustomText
                    text={submission.submittedAt ? formatSubmittedDate(submission.submittedAt) : "Not submitted"}
                    size="s"
                    style={styles.bodyText}
                    useTranslationText={false}
                  />
                </View>
                <View style={[styles.cell, styles.gradeCell]}>
                  <CustomText
                    text={
                      submission.totalPoints == null || submission.maximumPoints == null
                        ? "—"
                        : `${submission.totalPoints}/${submission.maximumPoints}`
                    }
                    size="s"
                    style={styles.bodyText}
                    useTranslationText={false}
                  />
                </View>
                <View style={[styles.cell, styles.postStatusCell]}>
                  <View
                    style={[
                      styles.statusBadge,
                      isPosted
                        ? styles.postedBadge
                        : isFailed
                          ? styles.failedBadge
                          : isPublishing
                            ? styles.pendingBadge
                            : styles.notPublishedBadge,
                    ]}>
                    <CustomText
                      text={postedStatusText}
                      size="xs"
                      useTranslationText={false}
                      style={
                        isPosted
                          ? styles.postedText
                          : isFailed
                            ? styles.failedText
                            : isPublishing
                              ? styles.pendingText
                              : styles.notPublishedText
                      }
                    />
                  </View>
                </View>
                <View style={[styles.cell, styles.actionsCell]}>
                  <CustomButton
                    text={buttonText}
                    size="s"
                    styleType="primary"
                    onPress={() => onLtiEditGradePress?.(submission)}
                    disabled={!onLtiEditGradePress}
                    style={styles.gradeButton}
                  />
                </View>
              </View>
            );
          })
        ) : (
          <View style={styles.emptyState}>
            <CustomText text="No data found" style={styles.emptyText} />
          </View>
        )}
      </View>
    </View>
  );
};

const GradingCenterTable: React.FC<GradingCenterTableProps> = ({ variant = "default", setModalState, ...props }) => {
  if (variant === "lti-assessment") {
    return <LtiAssessmentGradingTable {...props} />;
  }

  if (!setModalState) {
    return null;
  }

  return <DefaultGradingCenterTable setModalState={setModalState} />;
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  tableDataContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  checkboxCell: {
    width: 56,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 0,
  },
  studentCell: {
    flex: 2.5,
  },
  typeCell: {
    flex: 1.5,
  },
  courseCell: {
    flex: 1.5,
  },
  moduleCell: {
    flex: 1,
  },
  dateCell: {
    flex: 1.5,
  },
  statusCell: {
    flex: 1.5,
  },
  gradeCell: {
    flex: 1,
  },
  postStatusCell: {
    flex: 1.2,
  },
  actionsCell: {
    flex: 1,
    alignItems: "flex-start",
  },
  gradeButton: {
    minWidth: 92,
    height: 32,
  },
  headerText: {
    color: "#475569",
    fontSize: 12,
    letterSpacing: 0.5,
  },
  emptyState: {
    padding: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    color: "#6B7280",
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
    minHeight: 64,
  },
  userInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  userInitials: {
    color: "#FFFFFF",
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    color: "#111827",
    marginBottom: 2,
  },
  userEmail: {
    color: "#6B7280",
  },
  bodyText: {
    color: "#374151",
  },
  statusBadge: {
    alignSelf: "flex-start",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  postedBadge: {
    backgroundColor: "#DCFCE7",
  },
  pendingBadge: {
    backgroundColor: "#FEF3C7",
  },
  notPublishedBadge: {
    backgroundColor: "#F3F4F6",
  },
  failedBadge: {
    backgroundColor: "#FEE2E2",
  },
  postedText: {
    color: "#166534",
    fontWeight: "600",
  },
  pendingText: {
    color: "#92400E",
    fontWeight: "600",
  },
  notPublishedText: {
    color: "#4B5563",
    fontWeight: "600",
  },
  failedText: {
    color: "#991B1B",
    fontWeight: "600",
  },
});

export default GradingCenterTable;
